import { ref } from 'vue'
import type { AdminOrganisation, AdminOrganisationDetail } from '@/types/organisations'
import type { PaginationMeta } from '@/types/shared'
import { listOrganisations, fetchOrganisation } from '@/api/organisations'

export function useOrganisations() {
  const items = ref<AdminOrganisation[]>([])
  const meta = ref<PaginationMeta | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const search = ref('')
  const page = ref(1)
  const detail = ref<AdminOrganisationDetail | null>(null)

  async function fetchList(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const response = await listOrganisations({
        search: search.value || undefined,
        page: page.value,
        per_page: 25,
      })
      items.value = response.data
      meta.value = response.meta
    } catch {
      error.value = 'admin.common.error_loading'
    } finally {
      loading.value = false
    }
  }

  async function fetchDetail(id: number): Promise<void> {
    loading.value = true
    error.value = null
    try {
      detail.value = await fetchOrganisation(id)
    } catch (e: unknown) {
      if (isNotFoundError(e)) {
        error.value = 'admin.common.not_found'
      } else {
        error.value = 'admin.common.error_loading'
      }
    } finally {
      loading.value = false
    }
  }

  return { items, meta, loading, error, search, page, detail, fetchList, fetchDetail }
}

function isNotFoundError(e: unknown): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { status: number } }).response.status === 404
  )
}
